import { useState } from 'react';
import { AlertTriangle, ChevronRight, Info } from 'lucide-react';
import ModalShell from '../ui/ModalShell';

/**
 * „K dořešení" — co u položky nehraje. Norma §1f: blok patří do třetího
 * sloupce, jenže ten se na mobilu nekreslí. Co je v něm důležité, musí být
 * na telefonu dosažitelné jinak — řádkem pod hlavičkou detailu (nebo nad
 * seznamem), který otevře `ModalShell` zdola.
 *
 * Obě podoby kreslí tentýž `KDoreseniSeznam` z týchž položek, takže se panel
 * a mobilní modal nemůžou rozejít.
 *
 * Položka: `{ id, problem: boolean, titulek, popis? }` — `problem` = červeně
 * (jednoznačný problém), bez něj neutrální upozornění. Třetí barva není.
 */

const CERVENA = '#EF4444';

export function KDoreseniSeznam({ polozky = [], isDark, renderAkce = null, prazdne = 'Nic k dořešení.', kompaktni = false }) {
    if (!polozky.length) {
        return <p className="text-xs" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)' }}>{prazdne}</p>;
    }
    return (
        <div className="flex flex-col" style={{ gap: kompaktni ? 0 : 6 }}>
            {polozky.map(p => {
                const Ikona = p.problem ? AlertTriangle : Info;
                const akce = renderAkce?.(p);
                return (
                    <div key={p.id} className="flex"
                        style={kompaktni ? {
                            gap: 12, padding: '12px 16px',
                            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)'}`,
                        } : {
                            gap: 10, padding: '9px 10px', borderRadius: 8,
                            background: p.problem ? 'rgba(239,68,68,0.06)' : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                            border: `1px solid ${p.problem ? 'rgba(239,68,68,0.20)' : (isDark ? 'rgba(255,255,255,0.07)' : 'var(--border-subtle)')}`,
                        }}>
                        <Ikona size={15} style={{ color: p.problem ? CERVENA : 'var(--text-secondary)', flexShrink: 0, marginTop: 2 }} />
                        <div className="min-w-0 flex-1">
                            <p className="text-[12.5px] font-semibold" style={{ color: 'var(--text-primary)' }}>{p.titulek}</p>
                            {p.popis && <p className="text-[11.5px]" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)' }}>{p.popis}</p>}
                            {akce && <div style={{ marginTop: 8 }}>{akce}</div>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Mobilní řádek, který otevře modal s položkami. Když není co řešit, nekreslí
 * se vůbec — prázdný řádek by jen ukrajoval z obrazovky.
 *
 * @param {Array} polozky
 * @param {string} [titulek='K dořešení']
 * @param {React.ReactNode} [navic] - obsah modalu pod seznamem (zkrácený přehled)
 * @param {Function} [renderAkce] - akce u položky (Připomenout…)
 */
export default function KDoreseniRadek({ polozky = [], titulek = 'K dořešení', isDark, barva, navic = null, renderAkce = null, styl = null }) {
    const [otevreno, setOtevreno] = useState(false);
    if (!polozky.length) return null;
    const problemu = polozky.filter(p => p.problem).length;

    return (
        <>
            <button type="button" onClick={() => setOtevreno(true)}
                className="flex items-center w-full text-left"
                style={{
                    gap: 10, height: 48, padding: '0 12px', borderRadius: 10, cursor: 'pointer',
                    background: problemu ? 'rgba(239,68,68,0.08)' : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                    border: `1px solid ${problemu ? 'rgba(239,68,68,0.28)' : (isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)')}`,
                    color: 'var(--text-primary)', fontWeight: 600, fontSize: 14,
                    WebkitTapHighlightColor: 'transparent',
                    ...styl,
                }}>
                <AlertTriangle size={17} style={{ color: problemu ? CERVENA : 'var(--text-secondary)', flexShrink: 0 }} />
                <span className="flex-1 min-w-0 truncate">{titulek}</span>
                <span className="tabular-nums" style={{
                    fontSize: 12, fontWeight: 700, padding: '1px 8px', borderRadius: 99,
                    background: problemu ? 'rgba(239,68,68,0.22)' : (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)'),
                    color: problemu ? '#fecaca' : 'var(--text-secondary)',
                }}>{polozky.length}</span>
                <ChevronRight size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
            </button>

            {otevreno && (
                <ModalShell onClose={() => setOtevreno(false)} isDark={isDark} barva={barva}
                    ikona={AlertTriangle} nadpis={`${titulek} · ${polozky.length}`}
                    paticka={(
                        <button type="button" onClick={() => setOtevreno(false)} className="w-full rounded-xl font-semibold"
                            style={{ height: 46, background: barva, color: '#fff', border: 'none', cursor: 'pointer' }}>
                            Hotovo
                        </button>
                    )}>
                    <KDoreseniSeznam polozky={polozky} isDark={isDark} renderAkce={renderAkce} kompaktni />
                    {navic}
                </ModalShell>
            )}
        </>
    );
}
